import * as React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  Chip,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
} from "@mui/material";
import {
  ReceiptLong as InvoiceIcon,
  Receipt as ReceiptIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Save as SaveIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import { InvoiceFormData, InvoiceLine } from "../../types/billing.types";
import { InsuranceCard } from "../../types/resident.types";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import masterDataService from "../../services/masterDataService";
import billingService from "../../services/billingService";
import { getApiErrorMessage, normalizePreviousServices } from "./billingConfig";

interface InvoiceFormSectionProps {
  selectedResidentId: string;
  isReadOnly: boolean;
  invoiceForm: InvoiceFormData;
  updateInvoiceField: (field: keyof InvoiceFormData, value: any) => void;
  invoiceLines: InvoiceLine[];
  onSetInvoiceLines?: (lines: InvoiceLine[]) => void;
  onShowSnackbar?: (message: string, severity?: "success" | "error" | "warning" | "info") => void;
  handleInvoiceLineChange: (id: string, field: keyof InvoiceLine, value: any) => void;
  handleAddInvoiceLine: () => void;
  handleRemoveInvoiceLine: (id: string) => void;
  renderSectionHeader: (stepNumber: number | string, title: string, subtitle?: string) => React.ReactNode;
  sectionCardSx: any;
  onPrint?: () => void;
  residentInsurances?: InsuranceCard[];
  selectedInsuranceId?: string;
  onInsuranceCardSelect?: (id: string) => void;
  onCreateInvoice?: () => void;
  onSaveDraft?: () => void;
  isSubmitting?: boolean;
}

// Standard Assisted Living & Wrap Around Services for the dropdown
export interface ProductOrServiceOption {
  label: string;
  defaultDescription: string;
  defaultRate?: number | string;
  serviceLookupId?: string;
}

export const PRODUCT_OR_SERVICE_OPTIONS: ProductOrServiceOption[] = [
  {
    label: "Caregiving",
    defaultDescription: "",
    serviceLookupId: "e7f124ae-4512-4b70-a866-bea120d06f1e",
  },
  { label: "Wrap Around Services", defaultDescription: "Comprehensive In-Facility Wrap Around Care" },
  { label: "Room & Board", defaultDescription: "Assisted Living Room & Board" },
  { label: "Assisted Living Services", defaultDescription: "Daily Assisted Living & Support Care" },
  { label: "Personal Care", defaultDescription: "Activities of Daily Living (ADL) Care" },
  { label: "Medication Management", defaultDescription: "Daily Medication Administration & Supervision" },
  { label: "Skilled Nursing", defaultDescription: "Skilled Clinical Nursing Services" },
  { label: "Memory Care", defaultDescription: "Specialized Memory & Dementia Support" },
  { label: "Respite Care", defaultDescription: "Short-Term Respite Care Stay" },
  { label: "Hospice Care Support", defaultDescription: "Palliative & Comfort Care Support" },
  { label: "Physical Therapy", defaultDescription: "Physical Therapy & Rehabilitation" },
  { label: "Occupational Therapy", defaultDescription: "Occupational Therapy Services" },
  { label: "Speech Therapy", defaultDescription: "Speech & Language Therapy" },
  { label: "Dietary & Meal Plan", defaultDescription: "Specialized Senior Meal Program" },
  { label: "Transportation", defaultDescription: "Medical Escort & Transportation Services" },
  { label: "Private Duty Care", defaultDescription: "One-on-One Dedicated Caregiver" },
];

// Format numbers to currency display
const formatCurrency = (val: number | string): string => {
  const num = Number(val) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(num);
};

// Compact dropdown menu configuration (opens strictly below the box with a smooth custom scrollbar)
const compactMenuProps = {
  anchorOrigin: {
    vertical: "bottom" as const,
    horizontal: "left" as const,
  },
  transformOrigin: {
    vertical: "top" as const,
    horizontal: "left" as const,
  },
  PaperProps: {
    sx: {
      maxHeight: 220,
      mt: 0.5,
      borderRadius: "10px",
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)",
      border: "1px solid #E2E8F0",
      "&::-webkit-scrollbar": {
        width: "6px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "#F8FAFC",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#CBD5E1",
        borderRadius: "4px",
        "&:hover": {
          backgroundColor: "#94A3B8",
        },
      },
    },
  },
};

const parseStringToDate = (str: string): Date | null => {
  if (!str) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const parts = str.split("-");
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2].slice(0, 2), 10);
    const d = new Date(year, month, day);
    if (!isNaN(d.getTime())) return d;
  }
  const parts = str.split("/");
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
      const d = new Date(year, month, day);
      if (!isNaN(d.getTime())) return d;
    }
  }
  const fallback = new Date(str);
  return isNaN(fallback.getTime()) ? null : fallback;
};

const formatDateToString = (d: Date | null): string => {
  if (!d || isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

export const InvoiceFormSection: React.FC<InvoiceFormSectionProps> = ({
  selectedResidentId,
  isReadOnly,
  invoiceForm,
  updateInvoiceField,
  invoiceLines,
  onSetInvoiceLines,
  onShowSnackbar,
  handleInvoiceLineChange,
  handleAddInvoiceLine,
  handleRemoveInvoiceLine,
  renderSectionHeader,
  sectionCardSx,
  onPrint,
  residentInsurances = [],
  selectedInsuranceId = "",
  onInsuranceCardSelect,
  onCreateInvoice,
  onSaveDraft,
  isSubmitting = false,
}) => {
  const [customServiceRows, setCustomServiceRows] = useState<Record<string, boolean>>({});
  const [serviceOptions, setServiceOptions] = useState<ProductOrServiceOption[]>(PRODUCT_OR_SERVICE_OPTIONS);
  const [loadingPreviousServices, setLoadingPreviousServices] = useState(false);

  const handleFetchPreviousServices = async (residentIdToFetch?: string) => {
    const resId = residentIdToFetch || selectedResidentId;
    if (!resId) return;

    setLoadingPreviousServices(true);
    try {
      const data = await billingService.getResidentPreviousServices(resId);
      const lines = normalizePreviousServices(data);
      if (lines.length > 0) {
        if (onSetInvoiceLines) {
          onSetInvoiceLines(lines);
        }
        setServiceOptions((prev) => {
          const next = [...prev];
          lines.forEach((l) => {
            if (l.productOrService && !next.some((o) => o.label.toLowerCase() === l.productOrService.toLowerCase())) {
              next.push({
                label: l.productOrService,
                defaultDescription: l.description,
                defaultRate: l.rate,
                serviceLookupId: l.serviceLookupId,
              });
            }
          });
          return next;
        });
      }
    } catch (err: any) {
      console.warn("Failed to load resident previous services:", err);
    } finally {
      setLoadingPreviousServices(false);
    }
  };

  // Automatically fetch previous services when a resident is selected if lines are default (1 row with 0 rate)
  useEffect(() => {
    if (!selectedResidentId || isReadOnly) return;
    const isDefaultLines =
      invoiceLines.length === 0 ||
      (invoiceLines.length === 1 && (Number(invoiceLines[0].rate) === 0 || !invoiceLines[0].productOrService));

    if (isDefaultLines) {
      handleFetchPreviousServices(selectedResidentId);
    }
  }, [selectedResidentId, isReadOnly]);

  useEffect(() => {
    // Attempt to load lookup values for services from master data if configured
    masterDataService
      .getActiveLookupValuesByName("Service")
      .catch(() => masterDataService.getActiveLookupValuesByName("Services"))
      .catch(() => masterDataService.getActiveLookupValuesByName("CareServices"))
      .catch(() => masterDataService.getActiveLookupValuesByName("Care Services"))
      .then((lookupItems) => {
        if (Array.isArray(lookupItems) && lookupItems.length > 0) {
          const mapped: ProductOrServiceOption[] = lookupItems.map((item) => ({
            label: item.valueName,
            defaultDescription: item.description || item.valueName,
            serviceLookupId: item.id,
          }));
          setServiceOptions((prev) => {
            const existingLabels = new Set(mapped.map((m) => m.label.toLowerCase()));
            const remaining = prev.filter((p) => !existingLabels.has(p.label.toLowerCase()));
            return [...mapped, ...remaining];
          });
        }
      })
      .catch(() => {});
  }, []);

  const handleProductOrServiceSelect = (id: string, val: string) => {
    handleInvoiceLineChange(id, "productOrService", val);
    const matched = serviceOptions.find(
      (opt) => opt.label.toLowerCase() === val.toLowerCase()
    );
    if (matched) {
      if (matched.serviceLookupId) {
        handleInvoiceLineChange(id, "serviceLookupId", matched.serviceLookupId);
      }
      if (matched.defaultRate !== undefined && Number(matched.defaultRate) > 0) {
        const currentLine = invoiceLines.find((l) => l.id === id);
        if (!currentLine?.rate || Number(currentLine.rate) === 0) {
          handleInvoiceLineChange(id, "rate", matched.defaultRate);
        }
      }
    }
  };

  // Auto-format DD/MM/YYYY date input
  const handleDateInput = (field: keyof InvoiceFormData, rawVal: string) => {
    const digitsOnly = rawVal.replace(/\D/g, "").slice(0, 8);
    let formatted = digitsOnly;
    if (digitsOnly.length > 4) {
      formatted = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4)}`;
    } else if (digitsOnly.length > 2) {
      formatted = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
    }
    updateInvoiceField(field, formatted);
    if (field === "dueDate") {
      updateInvoiceField("overdueDate", formatted);
    }
  };

  // Prompt if resident is not selected yet
  if (!selectedResidentId) {
    return (
      <Box sx={{ mt: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: "center",
            borderRadius: 2.5,
            border: "1px dashed #BAE6FD",
            backgroundColor: "background.default",
          }}
        >
          <InvoiceIcon sx={{ fontSize: 52, color: "primary.main", mb: 1.5, opacity: 0.8 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary", mb: 0.5 }}>
            Please Select a Resident to View & Fill Invoice
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select a resident from the "Search Resident by Name" box above to display and populate the invoice claim fields.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const handlePrintDocument = () => {
    if (onPrint) {
      onPrint();
    } else {
      window.print();
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* ── Top Info Banner ── */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2.5,
          border: "1px solid #BAE6FD",
          backgroundColor: "#F0F9FF",
          "@media print": { display: "none" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.dark" }}>
            Individual Invoice
          </Typography>
          <Chip
            label="Assisted Living / Wrap Around"
            size="small"
            sx={{ backgroundColor: "#E0F2FE", color: "primary.dark", fontWeight: 600 }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Caregiving wrap-around services, resident room & board, and direct private pay billing
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {/* ═══════════════════════════════════════════════════════════════════════
            FORM EDITOR SECTION (INPUT FIELDS)
            ═══════════════════════════════════════════════════════════════════════ */}
        <Grid item xs={12} sx={{ "@media print": { display: "none" } }}>
            {/* Section 1: Resident & Care Demographics (Non-editable, auto-populated) */}
            <Paper
              elevation={0}
              sx={{
                ...sectionCardSx,
                "& .MuiOutlinedInput-root": { backgroundColor: "#F8FAFC" },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#1E293B",
                  fontWeight: 500,
                  cursor: "not-allowed",
                },
              }}
            >
              {renderSectionHeader(
                1,
                "Resident & Caregiving Metadata",
                "Resident identification, Medi-Cal CIN, dates of birth & coverage period (Auto-populated)"
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="invoice-resident-name"
                    label="Resident Name"
                    value={invoiceForm.residentName}
                    size="small"
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    id="invoice-resident-cin"
                    label="CIN (Policy / Member ID)"
                    value={invoiceForm.residentCin}
                    onChange={(e) => updateInvoiceField("residentCin", e.target.value)}
                    placeholder="Auto-fetched Policy / Member ID"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    disabled={isReadOnly}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    id="invoice-resident-dob"
                    label="Date of Birth"
                    value={invoiceForm.residentDob}
                    placeholder="DD/MM/YYYY"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    id="invoice-service-from"
                    label="Service Period From"
                    value={invoiceForm.servicePeriodFrom}
                    placeholder="DD/MM/YYYY"
                    size="small"
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    id="invoice-service-to"
                    label="Service Period To"
                    value={invoiceForm.servicePeriodTo}
                    placeholder="DD/MM/YYYY"
                    size="small"
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    id="invoice-managed-care"
                    label="Managed Care Medi-Cal Plan"
                    value={invoiceForm.managedCarePlan}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                    disabled
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Section 2: Bill To & Invoice Details */}
            <Paper elevation={0} sx={sectionCardSx}>
              {renderSectionHeader(
                2,
                "Billing Target & Invoice Details",
                "Recipient entity, billing addresses, invoice numbering and payment terms"
              )}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {residentInsurances.length > 1 ? (
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      sx={{
                        "& .MuiFormLabel-asterisk": { color: "#d32f2f !important" },
                      }}
                    >
                      <InputLabel shrink id="invoice-payer-name-label">
                        Client / Payer Name
                      </InputLabel>
                      <Select
                        id="invoice-bill-to-client"
                        labelId="invoice-payer-name-label"
                        label="Client / Payer Name"
                        notched
                        value={selectedInsuranceId || (residentInsurances[0]?.id || "0")}
                        onChange={(e) => onInsuranceCardSelect?.(e.target.value)}
                        disabled={isReadOnly}
                        MenuProps={compactMenuProps}
                      >
                        {residentInsurances.map((card, idx) => {
                          const valId = card.id || String(idx);
                          const name = card.insuranceCompanyName || card.provider || "Unnamed Insurance";
                          const typeTag = card.coverageType || card.insuranceType;
                          const labelText = typeTag ? `${name} (${typeTag})` : name;

                          return (
                            <MenuItem key={valId} value={valId}>
                              {labelText}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      id="invoice-bill-to-client"
                      label="Client / Payer Name"
                      value={invoiceForm.billToClientName}
                      onChange={(e) => updateInvoiceField("billToClientName", e.target.value)}
                      placeholder="Payer / Insurance Name (e.g. Anthem Blue Cross)"
                      size="small"
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                      disabled={isReadOnly}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="invoice-bill-to-address"
                    label="Bill To"
                    value={invoiceForm.billToAddress}
                    onChange={(e) => updateInvoiceField("billToAddress", e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    disabled={isReadOnly}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="invoice-ship-to-address"
                    label="Ship To"
                    value={invoiceForm.shipToAddress}
                    onChange={(e) => updateInvoiceField("shipToAddress", e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    disabled={isReadOnly}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    id="invoice-terms"
                    select
                    label="Payment Terms"
                    value={invoiceForm.terms}
                    onChange={(e) => updateInvoiceField("terms", e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ MenuProps: compactMenuProps }}
                    disabled={isReadOnly}
                  >
                    <MenuItem value="Net 15">Net 15</MenuItem>
                    <MenuItem value="Net 30">Net 30</MenuItem>
                    <MenuItem value="Net 60">Net 60</MenuItem>
                    <MenuItem value="Due on receipt">Due on receipt</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Invoice Date"
                      format="dd/MM/yyyy"
                      value={parseStringToDate(invoiceForm.invoiceDate)}
                      onChange={(newDate: Date | null) => {
                        if (newDate && !isNaN(newDate.getTime())) {
                          updateInvoiceField("invoiceDate", formatDateToString(newDate));
                        } else {
                          updateInvoiceField("invoiceDate", "");
                        }
                      }}
                      disabled={isReadOnly}
                      slotProps={{
                        textField: {
                          id: "invoice-date",
                          size: "small",
                          fullWidth: true,
                          required: true,
                          InputLabelProps: { shrink: true },
                          placeholder: "DD/MM/YYYY",
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Due Date"
                      format="dd/MM/yyyy"
                      value={parseStringToDate(invoiceForm.dueDate)}
                      onChange={(newDate: Date | null) => {
                        if (newDate && !isNaN(newDate.getTime())) {
                          const str = formatDateToString(newDate);
                          updateInvoiceField("dueDate", str);
                          updateInvoiceField("overdueDate", str);
                        } else {
                          updateInvoiceField("dueDate", "");
                        }
                      }}
                      disabled={isReadOnly}
                      slotProps={{
                        textField: {
                          id: "invoice-due-date",
                          size: "small",
                          fullWidth: true,
                          required: true,
                          InputLabelProps: { shrink: true },
                          placeholder: "DD/MM/YYYY",
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </Paper>

            {/* Section 3: Products & Services (Line Items Table) */}
            <Paper elevation={0} sx={sectionCardSx}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                {renderSectionHeader(
                  3,
                  "Products & Services (Line Items)",
                  "Billable care services, hourly/fixed wrap-around care, quantities and rates"
                )}
                {!isReadOnly && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={
                        loadingPreviousServices ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <HistoryIcon sx={{ fontSize: "1.1rem" }} />
                        )
                      }
                      onClick={() => handleFetchPreviousServices()}
                      disabled={loadingPreviousServices || !selectedResidentId}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        color: "#0284C7",
                        borderColor: "#BAE6FD",
                        "&:hover": { borderColor: "#0284C7", backgroundColor: "#F0F9FF" },
                      }}
                    >
                      {loadingPreviousServices ? "Loading Services..." : "Load Previous Services"}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddInvoiceLine}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        color: "#0284C7",
                        borderColor: "#BAE6FD",
                        "&:hover": { borderColor: "#0284C7", backgroundColor: "#F0F9FF" },
                      }}
                    >
                      Add Service Line
                    </Button>
                  </Box>
                )}
              </Box>

              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                      <TableCell sx={{ fontWeight: 700, width: 40, color: "#475569" }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 230, color: "#475569" }}>Product or service</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 90, color: "#475569" }}>Qty</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 130, color: "#475569" }}>Rate ($)</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 130, color: "#475569" }}>Amount ($)</TableCell>
                      {!isReadOnly && <TableCell sx={{ width: 50 }} />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoiceLines.map((row, idx) => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ fontWeight: 600, color: "#64748B" }}>{idx + 1}.</TableCell>
                        <TableCell sx={{ minWidth: 230 }}>
                          {customServiceRows[row.id] ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <TextField
                                size="small"
                                fullWidth
                                autoFocus
                                placeholder="Enter custom service"
                                value={row.productOrService}
                                onChange={(e) => handleInvoiceLineChange(row.id, "productOrService", e.target.value)}
                                disabled={isReadOnly}
                              />
                              <Tooltip title="Switch back to dropdown list">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setCustomServiceRows((prev) => ({ ...prev, [row.id]: false }));
                                    handleProductOrServiceSelect(row.id, "");
                                  }}
                                  sx={{ color: "#0284C7" }}
                                >
                                  <ArrowDropDownIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <TextField
                              select
                              size="small"
                              fullWidth
                              disabled={isReadOnly}
                              value={
                                serviceOptions.some((o) => o.label === row.productOrService)
                                  ? row.productOrService
                                  : (row.productOrService ? "__CUSTOM__" : "")
                              }
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === "__CUSTOM__") {
                                  setCustomServiceRows((prev) => ({ ...prev, [row.id]: true }));
                                  handleInvoiceLineChange(row.id, "productOrService", "");
                                } else {
                                  handleProductOrServiceSelect(row.id, val);
                                }
                              }}
                              SelectProps={{
                                displayEmpty: true,
                                renderValue: (selected: any) => {
                                  if (!selected) {
                                    return <span style={{ color: "#94A3B8", fontWeight: 400 }}>Select Product or Service</span>;
                                  }
                                  return selected;
                                },
                                MenuProps: compactMenuProps,
                              }}
                              sx={{
                                backgroundColor: "#FFFFFF",
                                borderRadius: "8px",
                                "& .MuiSelect-select": {
                                  fontWeight: row.productOrService ? 600 : 400,
                                  color: row.productOrService ? "#0F172A" : "#94A3B8",
                                },
                              }}
                            >
                              <MenuItem value="" disabled sx={{ fontSize: "0.82rem", color: "#94A3B8" }}>
                                <em>Select Product or Service...</em>
                              </MenuItem>
                              {serviceOptions.map((opt) => (
                                <MenuItem
                                  key={opt.serviceLookupId || opt.label}
                                  value={opt.label}
                                  sx={{
                                    fontSize: "0.82rem",
                                    py: 0.75,
                                    px: 1.5,
                                    "&:hover": {
                                      backgroundColor: "#F0F9FF",
                                    },
                                    "&.Mui-selected": {
                                      backgroundColor: "#E0F2FE !important",
                                      fontWeight: 600,
                                    },
                                  }}
                                >
                                  {opt.label}
                                </MenuItem>
                              ))}
                              <MenuItem
                                value="__CUSTOM__"
                                sx={{
                                  fontSize: "0.82rem",
                                  py: 0.85,
                                  px: 1.5,
                                  borderTop: "1px dashed #E2E8F0",
                                  color: "#0284C7",
                                  fontWeight: 600,
                                  backgroundColor: "#F8FAFC",
                                  "&:hover": {
                                    backgroundColor: "#F0F9FF",
                                  },
                                }}
                              >
                                + Custom / Other Service...
                              </MenuItem>
                            </TextField>
                          )}
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={row.description}
                            onChange={(e) => handleInvoiceLineChange(row.id, "description", e.target.value)}
                            disabled={isReadOnly}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={!row.qty || Number(row.qty) === 0 ? "" : row.qty}
                            onChange={(e) => handleInvoiceLineChange(row.id, "qty", e.target.value)}
                            inputProps={{ min: 1, step: 1 }}
                            disabled={isReadOnly}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={!row.rate || Number(row.rate) === 0 ? "" : row.rate}
                            onChange={(e) => handleInvoiceLineChange(row.id, "rate", e.target.value)}
                            inputProps={{ min: 0.01, step: "0.01" }}
                            disabled={isReadOnly}
                            error={Boolean(row.productOrService && (!row.rate || Number(row.rate) <= 0))}
                            helperText={
                              row.productOrService && (!row.rate || Number(row.rate) <= 0)
                                ? "Rate > 0 required"
                                : undefined
                            }
                            FormHelperTextProps={{
                              sx: { fontSize: "0.68rem", mx: 0, mt: 0.25, color: "#EF4444" },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#0F172A" }}>
                          {formatCurrency(row.amount)}
                        </TableCell>
                        {!isReadOnly && (
                          <TableCell align="center">
                            <Tooltip title={invoiceLines.length <= 1 ? "Clear this line" : "Delete this line"}>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveInvoiceLine(row.id)}
                                sx={{
                                  color: "#EF4444",
                                  "&:hover": { backgroundColor: "#FEE2E2", color: "#DC2626" },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              {/* Total Summary */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 3, mt: 3, pt: 2, borderTop: "1px solid #E2E8F0" }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: "text.secondary" }}>
                  Total Invoice Amount:
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0F172A" }}>
                  {formatCurrency(invoiceForm.totalAmount)}
                </Typography>
              </Box>
            </Paper>

            {/* Section 4: Payment Details & Notes */}
            <Paper elevation={0} sx={sectionCardSx}>
              {renderSectionHeader(
                4,
                "Payment Method & Processing Options",
                "Accepted payment modes, bank settlement info and customer notes"
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    id="invoice-payment-method"
                    select
                    label="Ways to Pay"
                    value={invoiceForm.paymentMethod}
                    onChange={(e) => updateInvoiceField("paymentMethod", e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ MenuProps: compactMenuProps }}
                    disabled={isReadOnly}
                  >
                    <MenuItem value="BANK">BANK / ACH Transfer</MenuItem>
                    <MenuItem value="CHECK">Check</MenuItem>
                    <MenuItem value="CREDIT_CARD">Credit Card</MenuItem>
                    <MenuItem value="WIRE">Wire Transfer</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    id="invoice-status-label"
                    label="Status Label"
                    value={invoiceForm.statusLabel || "Draft"}
                    onChange={(e) => updateInvoiceField("statusLabel", e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    disabled={isReadOnly}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    id="invoice-overdue-date"
                    label="Status / Due Date"
                    value={invoiceForm.overdueDate || invoiceForm.dueDate}
                    onChange={(e) => updateInvoiceField("overdueDate", e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    disabled={isReadOnly}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="invoice-notes"
                    label="Notes & Special Instructions"
                    value={invoiceForm.notes}
                    onChange={(e) => updateInvoiceField("notes", e.target.value)}
                    size="small"
                    multiline
                    rows={2}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    placeholder="Add any additional notes, remittance instructions or bank account details..."
                    disabled={isReadOnly}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Bottom Form Actions */}
            {!isReadOnly && (onCreateInvoice || onSaveDraft) && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 1.5,
                  mt: 3,
                  mb: 4,
                  "@media print": { display: "none" },
                }}
              >
                {onSaveDraft && (
                  <Button
                    id="save-draft-invoice-bottom-btn"
                    variant="outlined"
                    startIcon={isSubmitting ? <CircularProgress size={16} /> : <SaveIcon />}
                    disabled={isSubmitting || !selectedResidentId}
                    onClick={onSaveDraft}
                    sx={{ borderRadius: "12px", px: 2.5 }}
                  >
                    Save Draft
                  </Button>
                )}
                {onCreateInvoice && (
                  <Button
                    id="create-invoice-bottom-btn"
                    variant="contained"
                    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <ReceiptIcon />}
                    disabled={isSubmitting || !selectedResidentId}
                    onClick={onCreateInvoice}
                    sx={{
                      borderRadius: "12px",
                      px: 2.5,
                      boxShadow: "none",
                      backgroundColor: "#48CAE4",
                      color: "#000000",
                      fontWeight: 700,
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#3ab3cc", boxShadow: "none" },
                    }}
                  >
                    {isSubmitting ? "Creating Invoice..." : "Create Invoice"}
                  </Button>
                )}
              </Box>
            )}
          </Grid>

        {/* ═══════════════════════════════════════════════════════════════════════
            PRINTABLE INVOICE DOCUMENT (PRINT ONLY)
            ═══════════════════════════════════════════════════════════════════════ */}
        <Grid item xs={12} sx={{ display: "none", "@media print": { display: "block" } }}>
          <Box
            id="printable-invoice-paper"
              sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                border: "1px solid #E5E7EB",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
                p: { xs: 3, md: 5 },
                color: "#1F2937",
                fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                "@media print": {
                  boxShadow: "none",
                  border: "none",
                  p: 0,
                  m: 0,
                },
              }}
            >
              {/* ── Top Header Row ── */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 3 }}>
                {/* Left: Organization Details */}
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      letterSpacing: "1px",
                      color: "#1F2937",
                      fontSize: "1.55rem",
                      mb: 1.25,
                      textTransform: "uppercase",
                    }}
                  >
                    INVOICE
                  </Typography>

                  <Typography sx={{ fontWeight: 700, fontSize: "0.86rem", color: "#111827", lineHeight: 1.35 }}>
                    {invoiceForm.providerDba || "JSNU Healthcare LLC dba"}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.86rem", color: "#111827", lineHeight: 1.35, mb: 0.5 }}>
                    {invoiceForm.providerName || "Lakewood Villa Care Center"}
                  </Typography>

                  <Typography sx={{ fontSize: "0.8rem", color: "#374151", lineHeight: 1.4 }}>
                    {invoiceForm.providerAddress || "8708 Gerber Rd"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", color: "#374151", lineHeight: 1.4, mb: 1 }}>
                    {`${invoiceForm.providerCity || "Sacramento"}, ${invoiceForm.providerState || "CA"} ${invoiceForm.providerZip || "95828"}`}
                  </Typography>

                  <Typography sx={{ fontSize: "0.8rem", color: "#374151", lineHeight: 1.4 }}>
                    {invoiceForm.providerEmail || "jsnuhealthcare@gmail.com"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", color: "#374151", lineHeight: 1.4 }}>
                    {invoiceForm.providerPhone || "+1 (248) 294-8930"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", color: "#374151", lineHeight: 1.4 }}>
                    {invoiceForm.providerWebsite || "www.lakewoodvillacarecenter.com"}
                  </Typography>
                </Box>

                {/* Right: Care Center Logo Graphic */}
                <Box sx={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <svg width="150" height="90" viewBox="0 0 160 95" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Tree Leaves & House Graphic */}
                    <g opacity="0.65">
                      <path
                        d="M75 14C60 14 50 26 50 38C50 48 57 56 66 59L66 70H94L94 59C103 56 110 48 110 38C110 26 100 14 75 14Z"
                        fill="#A0AEC0"
                      />
                      <circle cx="56" cy="24" r="5" fill="#CBD5E0" />
                      <circle cx="98" cy="22" r="6" fill="#CBD5E0" />
                      <circle cx="106" cy="34" r="4" fill="#CBD5E0" />
                      <circle cx="48" cy="35" r="4" fill="#CBD5E0" />
                      {/* Hands & Care Figures Motif */}
                      <path
                        d="M80 32C76 32 73 35 73 39C73 43 76 46 80 46C84 46 87 43 87 39C87 35 84 32 80 32Z"
                        fill="#FFFFFF"
                      />
                      <path
                        d="M68 62C68 53 74 48 80 48C86 48 92 53 92 62H68Z"
                        fill="#FFFFFF"
                      />
                      <path
                        d="M65 52C60 56 59 62 65 67H95C101 62 100 56 95 52L88 56L80 50L72 56L65 52Z"
                        fill="#4A5568"
                      />
                    </g>
                    {/* Facility Brand Text */}
                    <text
                      x="80"
                      y="84"
                      textAnchor="middle"
                      fill="#4A5568"
                      fontSize="9.5"
                      fontWeight="700"
                      letterSpacing="0.8"
                      fontFamily="sans-serif"
                    >
                      LAKEWOOD VILLA
                    </text>
                    <text
                      x="80"
                      y="93"
                      textAnchor="middle"
                      fill="#718096"
                      fontSize="7"
                      fontWeight="500"
                      letterSpacing="1.2"
                      fontFamily="sans-serif"
                    >
                      CARE CENTER
                    </text>
                    {/* Underline Accents */}
                    <line x1="16" y1="87" x2="45" y2="87" stroke="#CBD5E0" strokeWidth="1" />
                    <line x1="115" y1="87" x2="144" y2="87" stroke="#CBD5E0" strokeWidth="1" />
                  </svg>
                </Box>
              </Box>

              {/* ── Shaded Header Band (Bill to / Ship to) ── */}
              <Box
                sx={{
                  backgroundColor: "#F3F4F6",
                  p: 2.5,
                  mt: 3,
                  mb: 2.5,
                  display: "flex",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 3,
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1F2937", mb: 0.5 }}>
                    {invoiceForm.billToClientName || invoiceForm.managedCarePlan || "Payer / Insurance Company"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#6B7280" }}>
                    Bill to
                  </Typography>
                  <Typography sx={{ fontSize: "0.85rem", color: "#1F2937", mt: 0.25 }}>
                    {invoiceForm.billToAddress || invoiceForm.billToClientName || "Payer Address"}
                  </Typography>
                </Box>

                <Box sx={{ pr: { md: 8 } }}>
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#6B7280" }}>
                    Ship to
                  </Typography>
                  <Typography sx={{ fontSize: "0.85rem", color: "#1F2937", mt: 0.25 }}>
                    {invoiceForm.shipToAddress || invoiceForm.billToClientName || "Payer Address"}
                  </Typography>
                </Box>
              </Box>

              {/* Dotted separator line */}
              <Box sx={{ borderBottom: "1px dotted #D1D5DB", mb: 2.5 }} />

              {/* ── Invoice Details Section ── */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#111827", mb: 0.75 }}>
                  Invoice details
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", color: "#374151", lineHeight: 1.5 }}>
                  Invoice no.: {invoiceForm.invoiceNumber || "—"}
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", color: "#374151", lineHeight: 1.5 }}>
                  Terms: {invoiceForm.terms || "Net 15"}
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", color: "#374151", lineHeight: 1.5 }}>
                  Invoice date: {invoiceForm.invoiceDate || "—"}
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", color: "#374151", lineHeight: 1.5 }}>
                  Due date: {invoiceForm.dueDate || "—"}
                </Typography>
              </Box>

              {/* ── Product or Service Table ── */}
              <Box sx={{ mb: 2 }}>
                <Table size="small" sx={{ "& td, & th": { borderBottom: "none", py: 1.25 } }}>
                  <TableHead>
                    <TableRow sx={{ borderBottom: "1.5px solid #111827" }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#111827", width: "4%", pl: 0 }}>
                        #
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#111827", width: "24%" }}>
                        Product or service
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#111827", width: "36%" }}>
                        Description
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#111827", width: "10%" }}>
                        Qty
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#111827", width: "13%" }}>
                        Rate
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#111827", width: "13%", pr: 0 }}>
                        Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoiceLines.map((line, idx) => (
                      <TableRow key={line.id} sx={{ borderBottom: "1px solid #E5E7EB" }}>
                        <TableCell sx={{ fontSize: "0.82rem", color: "#111827", pl: 0, verticalAlign: "top" }}>
                          {idx + 1}.
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#111827", verticalAlign: "top" }}>
                          {line.productOrService || "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.82rem", color: "#374151", verticalAlign: "top" }}>
                          {line.description || "—"}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: "0.82rem", color: "#111827", verticalAlign: "top" }}>
                          {line.qty || 1}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: "0.82rem", color: "#111827", verticalAlign: "top" }}>
                          {formatCurrency(line.rate)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: "0.82rem", color: "#111827", pr: 0, verticalAlign: "top" }}>
                          {formatCurrency(line.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* ── Resident Care Details Box Below Line Item ── */}
                <Box
                  sx={{
                    borderTop: "1px solid #E5E7EB",
                    borderBottom: "1px solid #E5E7EB",
                    py: 1.5,
                    px: 1.5,
                    my: 1.5,
                    backgroundColor: "#FAFAFA",
                  }}
                >
                  <Typography sx={{ fontSize: "0.76rem", color: "#1F2937", lineHeight: 1.6, mb: 0.5 }}>
                    <strong>Resident Name:</strong> {invoiceForm.residentName || "—"}{" "}
                    &nbsp;&nbsp;<strong>CIN:</strong> {invoiceForm.residentCin || "—"}{" "}
                    &nbsp;&nbsp;<strong>Date of Birth:</strong> {invoiceForm.residentDob || "—"}{" "}
                    &nbsp;&nbsp;<strong>Service Period:</strong>{" "}
                    {invoiceForm.servicePeriodFrom || invoiceForm.servicePeriodTo
                      ? `${invoiceForm.servicePeriodFrom || "—"} - ${invoiceForm.servicePeriodTo || "—"}`
                      : "—"}{" "}
                    &nbsp;&nbsp;<strong>Managed Care Medi-Cal Plan:</strong>{" "}
                    {invoiceForm.managedCarePlan || "—"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.76rem", color: "#1F2937", lineHeight: 1.6 }}>
                    <strong>Assisted Living Service Provider:</strong>{" "}
                    {invoiceForm.providerName || "Lakewood Villa Care Center"}{" "}
                    &nbsp;&nbsp;<strong>License #</strong> {invoiceForm.providerLicenseNo || "342701553"}{" "}
                    &nbsp;&nbsp;<strong>NPI:</strong> {invoiceForm.providerNpi || "1558178681"}
                  </Typography>
                </Box>
              </Box>

              {/* ── Ways to Pay & Totals ── */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  pt: 2,
                  flexWrap: "wrap",
                  gap: 3,
                }}
              >
                {/* Left: Ways to Pay & Button */}
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1F2937", mb: 1 }}>
                    Ways to pay
                  </Typography>
                  <Box
                    sx={{
                      display: "inline-block",
                      border: "1px solid #D1D5DB",
                      borderRadius: "3px",
                      px: 1,
                      py: 0.35,
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      color: "#4B5563",
                      letterSpacing: "0.5px",
                      mb: 3,
                    }}
                  >
                    {invoiceForm.paymentMethod || "BANK"}
                  </Box>

                  <Box>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "#4B5563",
                        color: "#FFFFFF",
                        borderRadius: "24px",
                        px: 3.5,
                        py: 1,
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        textTransform: "none",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "#374151",
                          boxShadow: "none",
                        },
                      }}
                      onClick={handlePrintDocument}
                    >
                      View and pay
                    </Button>
                  </Box>
                </Box>

                {/* Right: Total Amount & Overdue Status */}
                <Box sx={{ textAlign: "right", minWidth: 220 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: "#111827" }}>
                      Total
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: "1.45rem", color: "#111827" }}>
                      {formatCurrency(invoiceForm.totalAmount)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#6B7280" }}>
                      {invoiceForm.statusLabel || "Draft"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.82rem", color: "#374151" }}>
                      {invoiceForm.overdueDate || invoiceForm.dueDate || "—"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
      </Grid>
    </Box>
  );
};

export default InvoiceFormSection;
